import ClassCard from "../SelectedClassesListStep/ClassCard";
import { SeedCluster, SeedItem } from "hooks/LabelPropagation/useGetSeed";
import { ProgressCardProps } from "./ProgressCard";
import VLCollapseCard, {
  CollapseCardItem,
} from "views/uikit/Cards/VLCollapseCard";
import { ReactNode, useMemo } from "react";
import VLMessage from "views/uikit/VLMessage";
import ProgressCardList from "./ProgressCardList";
import { useLocation, useNavigate, useParams } from "react-router";
import { StatsState } from "hooks/LabelPropagation/useGetStats";
import {
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_PARAMETER,
  SEED_PARAMETER,
} from "helpers/constants/miscellaneous";
import { LoadingOutlined } from "@ant-design/icons";
import { formatNumber } from "helpers/utility/formatters";
import VLColorfulLoader from "views/uikit/VLColorfulLoader";
import { useExportSelection } from "hooks/useExportSelection";
import VLProgressBar from "views/uikit/ProgressBars/VLProgressBar";
import styles from "./style.module.scss";

export interface ProgressCardsProps {
  mainProgress: ProgressCardProps;
  items: ProgressCardProps[];
  totalCount?: string;
}

interface ProgressStepProps {
  initialFlowId?: string | null;
  seedId?: string | null;
  stats?: StatsState;
  loadingType?: LoadingType;
  seed?: SeedCluster[];
}

export type LoadingType = "RUN" | "SUBMIT" | "FINISH";

const CARD_KEYS = {
  REVIEW: "review",
  TRAIN_GOAL: "train-worthy-goal",
  SEED: "seed",
} as const;

export default function ProgressStep({
  initialFlowId,
  seedId,
  loadingType,
  stats,
  seed,
}: ProgressStepProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { datasetId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isReviewOpen = searchParams.get(REVIEW_PARAMETER) === "true";

  const { handleClearAll } = useExportSelection();

  const activeCards: string[] = useMemo(() => {
    let cards: string[] = [];
    if (stats?.review) {
      cards.push(CARD_KEYS.REVIEW);
    }

    return cards;
  }, [stats]);

  const items: CollapseCardItem[] = [
    ...(stats?.review && loadingType !== "SUBMIT"
      ? [
          {
            header: { label: "Review", progress: stats.review.mainProgress },
            key: CARD_KEYS.REVIEW,
            textButton: stats.review.totalCount,
            children: !isReviewOpen ? (
              <VLMessage
                onClick={() => {
                  const flow = initialFlowId;
                  if (!datasetId || !seedId || !flow) return;
                  const searchParams = new URLSearchParams(location.search);
                  searchParams.set(SEED_PARAMETER, seedId);
                  searchParams.set(FLOW_PARAMETER, flow);
                  searchParams.set(REVIEW_PARAMETER, "true");
                  handleClearAll();
                  navigate(
                    {
                      pathname: `/dataset/${datasetId}/data`,
                      search: searchParams.toString(),
                    },
                    { replace: false },
                  );
                }}
                title="New images are waiting for your review"
                description="A new set of images is ready for your review. Label Propagation is paused until this review is completed."
                textButton="Review"
              />
            ) : (
              <ProgressCardList items={stats.review.items} />
            ),
          },
        ]
      : []),
    {
      header: {
        label: "Train-worthy Goal",
        progress: stats?.labels?.mainProgress,
      },
      textButton: stats?.labels?.totalCount,
      key: CARD_KEYS.TRAIN_GOAL,
      children: stats?.labels ? (
        <ProgressCardList items={stats.labels.items} />
      ) : null,
    },
    {
      header: { label: "Seed" },
      key: CARD_KEYS.SEED,
      textButton: formatNumber(
        seed?.reduce((total, s) => total + s.items.length, 0) || 0,
      ),
      children: (
        <div className={styles.seedWrapper}>
          {seed?.map((seed: SeedCluster, clusterIdx: number) => (
            <div key={seed.label + clusterIdx} className={styles.seedItem}>
              <ClassCard
                items={seed.items.map((sample: SeedItem) => ({
                  id: sample.id,
                  imgUri: sample.imgUri,
                }))}
                value={seed.label}
                classId="null"
                min={5}
                progressValue={seed.count}
              />
            </div>
          ))}
        </div>
      ),
    },
  ];

  const mainProgress = stats?.mainProgress;

  const renderProgressContent = () => {
    if (!mainProgress) {
      return (
        <div className={styles.progressContentSpinner}>
          <LoadingOutlined spin={false} />
        </div>
      );
    }

    return (
      <div className={styles.mainProgressContent}>
        {loadingType === "SUBMIT" && <LoadingOutlined spin={false} />}
        {`${mainProgress.itemLength}/${mainProgress.maxItems} Labelled`}
      </div>
    );
  };

  const renderLoaderByType: Record<LoadingType, ReactNode | undefined> = {
    RUN: <VLColorfulLoader text={"Running Label Propagation..."} />,
    SUBMIT: undefined,
    FINISH: (
      <VLColorfulLoader
        text={"Applying your feedback - label propagation in progress..."}
      />
    ),
  };
  const isReviewSubmitting = loadingType === "SUBMIT";

  if (loadingType && !isReviewSubmitting) {
    return (
      <div className={styles.mainLoader}>{renderLoaderByType[loadingType]}</div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.mainProgress}>
        <VLProgressBar
          type={"primary"}
          isActive={isReviewSubmitting}
          maxItems={mainProgress?.maxItems || 0}
          itemLength={mainProgress?.itemLength || 0}
          color={
            isReviewSubmitting ? undefined : mainProgress?.color || undefined
          }
        />
        {renderProgressContent()}
      </div>

      {
        <VLCollapseCard
          items={items}
          defaultOpenKeys={activeCards}
          onClose={(key) => {
            if (key === "review") {
              const searchParams = new URLSearchParams(location.search);
              searchParams.delete(REVIEW_PARAMETER);
              searchParams.delete(LABEL_CLASS_PARAMETER);
              navigate(
                {
                  pathname: location.pathname,
                  search: searchParams.toString(),
                },
                { replace: true },
              );
            }
          }}
        />
      }
    </div>
  );
}
